import type { QueryClient } from "@tanstack/react-query";
import type { AdminRouteV2ControllerGetRoutesPagedQueryParams } from "service/api/estabrookComponents";
import type { AdminGetRoutesResDto } from "service/api/estabrookSchemas";

export type RouteUpdatedPayload = {
  date: string;
  route: AdminGetRoutesResDto;
};

export type RoutesListParams = {
  date?: string;
  includeLocations?: boolean;
  orderStatuses?: unknown[];
  locationTypes?: unknown[];
};

const ROUTES_LIST_PATH = ["api", "v2", "admin", "routes"];

/**
 * The routes list for a date: ["api","v2","admin","routes", queryParams].
 * Longer keys under /routes (state/check, notifications, ...) don't match.
 */
const routesListParams = (
  queryKey: readonly unknown[],
  date: string,
): RoutesListParams | null => {
  if (
    queryKey.length !== ROUTES_LIST_PATH.length + 1 ||
    ROUTES_LIST_PATH.some((part, index) => queryKey[index] !== part)
  ) {
    return null;
  }

  const params = queryKey[ROUTES_LIST_PATH.length];
  if (!params || typeof params !== "object") {
    return null;
  }

  const listParams = params as RoutesListParams;
  return listParams.date === date ? listParams : null;
};

const hasStopFilters = (params: RoutesListParams): boolean =>
  Boolean(params.orderStatuses?.length || params.locationTypes?.length);

/** A routes list for the date that shows every stop, so a route can be swapped in as sent. */
export const isUnfilteredRoutesList = (
  queryKey: readonly unknown[],
  date: string,
): boolean => {
  const params = routesListParams(queryKey, date);
  return params !== null && !hasStopFilters(params);
};

/** A routes list for the date whose stops the server filters, so a route can't be swapped in as sent. */
export const isFilteredRoutesList = (
  queryKey: readonly unknown[],
  date: string,
): boolean => {
  const params = routesListParams(queryKey, date);
  return params !== null && hasStopFilters(params);
};

const replaceRoute = (
  routes: AdminGetRoutesResDto[],
  route: AdminGetRoutesResDto,
): AdminGetRoutesResDto[] | null => {
  const index = routes.findIndex(({ id }) => id === route.id);
  if (index === -1) {
    return null;
  }

  const next = [...routes];
  // The server numbers a driver's routes across the whole list, so a single
  // route can't carry the right number; a tap never changes it anyway.
  next[index] = { ...route, priorityByDriver: routes[index].priorityByDriver };
  return next;
};

/**
 * Returns the cached list with the route replaced, or null when the list
 * doesn't contain it (then the caller falls back to a refetch).
 * Handles both the paged shape ({ data, metaData }) and a plain array.
 */
export const replaceRouteInList = (
  cached: unknown,
  route: AdminGetRoutesResDto,
): unknown | null => {
  if (Array.isArray(cached)) {
    return replaceRoute(cached as AdminGetRoutesResDto[], route);
  }

  const paged = cached as { data?: AdminGetRoutesResDto[] } | undefined;
  if (!paged || !Array.isArray(paged.data)) {
    return null;
  }

  const data = replaceRoute(paged.data, route);
  return data ? { ...paged, data } : null;
};

/**
 * Fetches just these routes, the way each cached list of the date was fetched,
 * and swaps them in, so an update to one route never reloads the whole date.
 * A list that doesn't hold one of them, or a failed request, falls back to
 * refetching that list. A route that arrived through ADMIN_ROUTE_UPDATED
 * after the request started is newer than the response and is left alone.
 */
export const refreshRoutesInCachedLists = async ({
  queryClient,
  fetchRoutes,
  date,
  routeIds,
  listFilter = () => true,
  arrivedAt,
}: {
  queryClient: QueryClient;
  fetchRoutes: (
    queryParams: AdminRouteV2ControllerGetRoutesPagedQueryParams,
  ) => Promise<AdminGetRoutesResDto[]>;
  date: string;
  routeIds: string[];
  listFilter?: (queryKey: readonly unknown[]) => boolean;
  arrivedAt: (routeId: string) => number | undefined;
}): Promise<void> => {
  if (routeIds.length === 0) return;

  const startedAt = Date.now();
  const listsByParams = new Map<string, (readonly unknown[])[]>();
  for (const query of queryClient.getQueryCache().findAll()) {
    const params = routesListParams(query.queryKey, date);
    if (!params || !listFilter(query.queryKey)) continue;
    const fetchParams = JSON.stringify({
      includeLocations: params.includeLocations,
      orderStatuses: params.orderStatuses,
      locationTypes: params.locationTypes,
    });
    listsByParams.set(fetchParams, [
      ...(listsByParams.get(fetchParams) ?? []),
      query.queryKey,
    ]);
  }

  await Promise.all(
    [...listsByParams].map(async ([fetchParams, queryKeys]) => {
      let routes: AdminGetRoutesResDto[];
      try {
        routes = await fetchRoutes({
          ...(JSON.parse(
            fetchParams,
          ) as AdminRouteV2ControllerGetRoutesPagedQueryParams),
          date,
          ids: routeIds,
          page: 1,
          limit: routeIds.length,
        });
      } catch {
        queryKeys.forEach((queryKey) =>
          queryClient.invalidateQueries({ queryKey, exact: true }),
        );
        return;
      }

      const fresh = routes.filter(
        ({ id }) => (arrivedAt(id) ?? 0) <= startedAt,
      );
      const missing = routeIds.some(
        (routeId) => !routes.some(({ id }) => id === routeId),
      );
      queryKeys.forEach((queryKey) => {
        let complete = !missing;
        queryClient.setQueryData(queryKey, (cached: unknown) =>
          fresh.reduce<unknown>((list, route) => {
            const next = replaceRouteInList(list, route);
            if (next === null) {
              complete = false;
              return list;
            }
            return next;
          }, cached),
        );
        if (!complete) {
          queryClient.invalidateQueries({ queryKey, exact: true });
        }
      });
    }),
  );
};
